import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", company: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await signup(form);
      toast.success("Welcome to LeadsB2B — let's build your first list.");
      nav("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signup failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#FAFAF7]">
      <div className="hidden lg:flex flex-col justify-between p-12 dark-section relative overflow-hidden">
        <div className="absolute inset-0 dot-bg-dark opacity-50" />
        <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full bg-[#FF5722]/20 blur-3xl" />
        <div className="relative"><Brand light /></div>
        <div className="relative">
          <div className="label-mono !text-white/50 mb-4">— Free trial</div>
          <div className="font-display text-5xl font-black tracking-tighter leading-tight text-white">
            50 prospects on us.<br />No card required.
          </div>
          <p className="mt-6 text-white/70 max-w-sm">Build your first scored prospect list in under 90 seconds.</p>
        </div>
        <div className="relative text-xs font-mono uppercase tracking-widest text-white/40">© 2026 LeadsB2B</div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Brand /></div>
          <div className="label-mono mb-3">— Get started</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-8">Create your account.</h1>
          <div className="space-y-5">
            <div>
              <Label htmlFor="name" className="label-mono">Full name</Label>
              <Input id="name" required value={form.name} onChange={set("name")} className="mt-2 h-11 rounded-lg border-[#EAEAE3] bg-white" data-testid="signup-name" />
            </div>
            <div>
              <Label htmlFor="email" className="label-mono">Work email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} className="mt-2 h-11 rounded-lg border-[#EAEAE3] bg-white" data-testid="signup-email" />
            </div>
            <div>
              <Label htmlFor="company" className="label-mono">Company (optional)</Label>
              <Input id="company" value={form.company} onChange={set("company")} className="mt-2 h-11 rounded-lg border-[#EAEAE3] bg-white" data-testid="signup-company" />
            </div>
            <div>
              <Label htmlFor="password" className="label-mono">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-2 h-11 rounded-lg border-[#EAEAE3] bg-white" data-testid="signup-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 rounded-lg bg-[#0A0A0F] hover:bg-[#14141B] text-white font-medium" data-testid="signup-submit">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </div>
          <div className="mt-6 text-sm text-[#5C5C66]">
            Already have an account?{" "}
            <Link to="/login" className="text-[#0A0A0F] font-medium underline underline-offset-4 hover:text-[#002FA7]" data-testid="signup-to-login">Sign in</Link>
          </div>
          <p className="mt-4 text-xs text-[#9A9AA3] text-center">
            By creating an account you agree to our{" "}
            <Link to="/terms" className="underline hover:text-[#0A0A0F]">Terms of Service</Link>
            {" "}and{" "}
            <Link to="/privacy" className="underline hover:text-[#0A0A0F]">Privacy Policy</Link>.
          </p>
        </form>
      </div>
    </div>
  );
}
